import React, { useEffect, useRef } from 'react';
import { Alert } from '@manulife/mux';
import { useConfig } from '../hooks';
import { get } from 'lodash';

export enum ErrorType {
  SPONSOR_ERROR = 'SPONSOR_ERROR',
  CREATE_ADVISOR_ERROR = 'CREATE_ADVISOR_ERROR'
}

interface ErrorCardProps {
  hasError: boolean;
  errorType: ErrorType;
}

const getErrorTitle = (labels, errorType: ErrorType) => {
  switch (errorType) {
    case ErrorType.SPONSOR_ERROR:
      return get(labels, 'formErrors.reqAdvisorCode');
    case ErrorType.CREATE_ADVISOR_ERROR:
      return get(labels, 'formErrors.unableToCompleteAddUserError');
    default:
      return null;
  }
};

const getErrorBody = (labels, errorType: ErrorType) => {
  if (errorType === ErrorType.CREATE_ADVISOR_ERROR) {
    return get(labels, 'formErrors.addUserTryAgainLater');
  }
  return null;
};

/**
 * ErrorCard Component
 *
 * Displays error messages in an alert card with auto-scroll functionality.
 * Automatically scrolls the error into view when displayed.
 *
 * @param {boolean} hasError - Whether to display the error card
 * @param {ErrorType} errorType - The type of error to display
 */
const ErrorCard: React.FC<ErrorCardProps> = ({ hasError, errorType }) => {
  const { labels } = useConfig();
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasError) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hasError]);

  return (
    <div className="advisor-error-card" ref={errorRef} style={{ maxWidth: '816px' }}>
      <Alert severity="alert" style={{ marginTop: 'var(--cds-layout-5)' }} title={getErrorTitle(labels, errorType)}>
        {getErrorBody(labels, errorType)}
      </Alert>
    </div>
  );
};

export default ErrorCard;
